// Hideo - Agenda: the work plan towards the end of work (= publish date and time) and the items of the Home box
// "To do and overdue" (src/components/HomeAgenda.vue). The same plan gives the work deadlines of the calendar file (.ics,
// src/features/ics.ts) and the work dates give the Timeline its bars (TimelinePage.vue) and the video dialog its work line.
//
// Plan without a work start (days before publication): Idea → "Start the script" by 7 days before · In Script →
// "Finish the script" by 5 · In Registrazione → "Record" by 3 · In Montaggio → "Finish editing" by 1 ·
// Ready = done. With a work start the deadlines split the period (agPlan); the texts change with the Type (AG_STEP_TXT).
// A video with a custom status (not in STATUS_KEYS) is left out of the plan.
import { fmtDate, t, tn, tv } from '../i18n'
import { STATUS_KEYS, STATUS_READY, formatKind } from '../data/contentTypes'
import type { ProfileData, Video } from '../data/types'
import { dayOf, isoDay } from '../utils/date'
import { WORK_TIME_RE } from '../utils/date'

// ===== Work dates (shared with the Timeline) =====
/** "YYYY-MM-DDTHH:MM" (local time) → Date, or null */
export const tlParse = (s: unknown): Date | null => (typeof s === 'string' && WORK_TIME_RE.test(s) ? new Date(s) : null)
/** End of work = publish day and time (end of the day without a time), or null without a valid date */
export const tlEnd = (v: Pick<Video, 'publishDate' | 'publishTime'>): Date | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(v.publishDate || '') ? new Date(`${v.publishDate}T${/^\d{2}:\d{2}$/.test(v.publishTime || '') ? v.publishTime : '23:59'}`) : null
/** Work start, only when valid and before the end of work */
export const tlStart = (v: Pick<Video, 'publishDate' | 'publishTime' | 'workStart'>): Date | null => {
  const s = tlParse(v.workStart), e = tlEnd(v)
  return s && e && s < e ? s : null
}
/** Readable duration: "3 hours", "2 days" */
export function tlDur(ms: number): string {
  const h = Math.round(ms / 3600000)
  if (h < 24) return h < 1 ? t('less than an hour') : tn(h, '{n} hour', '{n} hours')
  return tn(Math.round(h / 24), '{n} day', '{n} days')
}

// ===== Plan =====
interface AgStep {
  days: number
  txt: string
  icon: string
  /** Share of the work period (with a work start); default AG_SHARE */
  share?: number
  /** A step the Type does not have (Blog) */
  skip?: boolean
}
interface AgPlanStep extends AgStep {
  /** Deadline, YYYY-MM-DD */
  due: string
}
export const AG_STEPS: AgStep[] = [
  { days: 7, txt: t('Start the script'), icon: 'fa-lightbulb' },
  { days: 5, txt: t('Finish the script'), icon: 'fa-pen-nib' },
  { days: 3, txt: t('Record'), icon: 'fa-video' },
  { days: 1, txt: t('Finish editing'), icon: 'fa-scissors' }
]
/** The Home box looks at releases in the next 14 days (plus past ones that are not ready) */
const AG_WINDOW = 14
/** With a work start: script started at the start, then at 35% and 65% of the period, editing finished at the end */
const AG_SHARE = [0, 0.35, 0.65, 1]
type StepText = [string, string, number?, number?] | null
/** The same four deadlines, in each Type's words (statuses per Type: STATUS_NAMES in src/data/contentTypes.ts) */
const AG_STEP_TXT: Record<string, StepText[]> = {
  post: [[t('Start the post'), 'fa-lightbulb'], [t('Finish text and idea'), 'fa-pen-nib'], [t('Create photo or graphics'), 'fa-image'], [t('Review the post'), 'fa-check-double']],
  // Blog: only two deadlines (Idea → Writing → Ready): [text, icon, days before, share of the period]; null = missing step
  blog: [[t('Start the article'), 'fa-lightbulb', 5, 0], [t('Finish and publish'), 'fa-pen-nib', 1, 1], null, null],
  live: [[t('Start the run sheet'), 'fa-lightbulb'], [t('Finish the run sheet'), 'fa-list-ol'], [t('Do the tech check'), 'fa-sliders'], [t('Announce the live stream'), 'fa-bullhorn']]
}

// Dates as YYYY-MM-DD in local time (src/utils/date.ts)
export const agAdd = (iso: string, n: number): string => {
  const d = dayOf(iso)
  d.setDate(d.getDate() + n)
  return isoDay(d)
}
/** Days from b to a */
export const agDiff = (a: string, b: string): number => Math.round((+dayOf(a) - +dayOf(b)) / 86400000)
/** Short date with the weekday ("Wed 14/10") */
export const agShort = (iso: string): string => fmtDate(dayOf(iso), { weekday: 'short', day: '2-digit', month: '2-digit' })

/** Checklist items still unticked (names from the profile Settings; default names shown in the active language) */
function agMissing(d: ProfileData, v: Video): string[] {
  const chk = v.checklist || {}
  return d.settings.checklist.filter(it => !chk[it.id]).map(it => String(tv(it.label)))
}
/** The four steps of a video, in its Type's words */
export function agSteps(v: Pick<Video, 'format'>): AgStep[] {
  const T = AG_STEP_TXT[formatKind(v.format)]
  if (!T) return AG_STEPS
  return AG_STEPS.map((st, i) => {
    const x = T[i]
    return x ? { ...st, txt: x[0], icon: x[1], days: x[2] ?? st.days, share: x[3] } : { ...st, skip: true }
  })
}
/** Plan deadlines (one per step), or null without an end of work. With a work start too the period is split
 *  (AG_SHARE); without one, backwards from the end of work (7, 5, 3 and 1 days before). */
export function agPlan(v: Video): AgPlanStep[] | null {
  const ws = tlStart(v), we = tlEnd(v), S = agSteps(v)
  if (ws && we) return S.map((st, i) => ({ ...st, due: isoDay(new Date(+ws + (+we - +ws) * (st.share ?? AG_SHARE[i]))) }))
  return we ? S.map(st => ({ ...st, due: agAdd(v.publishDate as string, -st.days) })) : null
}
/** Next step of a video, with late = days late (0 = today, < 0 = days left); null when finished or without a plan */
export function agNextStep(v: Video, today: string): (AgPlanStep & { late: number }) | null {
  const s = (STATUS_KEYS as readonly string[]).indexOf(v.status || 'Idea'), plan = agPlan(v)
  if (s < 0 || s >= 4 || !plan) return null
  const st = plan[s]
  return { ...st, late: agDiff(today, st.due) }
}

type AgendaKind = 'overdue' | 'now' | 'today' | 'soon' | 'check'
interface AgendaItem {
  v: Video
  kind: AgendaKind
  rank: number
  /** Sort key: publish day and time */
  ref: string
  /** Checklist items still unticked (only in the last 7 days) */
  miss: string[]
  /** Days to the end of work (not for overdue) */
  left?: number
  /** Overdue: milliseconds since the end of work */
  ms?: number
  step?: AgPlanStep & { late: number }
}
/** Items of the Home box, from the videos of the active project. "Overdue" is ONLY a video whose end of work (publish day
 *  and time) is already in the past and that is not ready (user decision). The plan's intermediate deadlines are
 *  suggestions: a missed one becomes "To do now", never overdue. Then: today's and the next days' steps, incomplete
 *  checklists. Published videos are finished and left out. */
export function agendaItems(d: ProfileData, videos: Video[], now: Date): AgendaItem[] {
  const today = isoDay(now), out: AgendaItem[] = []
  videos.forEach(v => {
    const we = tlEnd(v)
    if (!we || v.published) return
    const pd = v.publishDate as string
    const ready = v.status === STATUS_READY, left = agDiff(pd, today), ref = pd + (v.publishTime || '99')
    if (left > AG_WINDOW) return
    const miss = left <= 7 ? agMissing(d, v) : []
    if (ready) {
      if (left >= 0 && miss.length) out.push({ v, kind: 'check', rank: 3, left, miss, ref })
      return
    }
    if (we < now) {
      out.push({ v, kind: 'overdue', rank: 0, ms: +now - +we, miss, ref })
      return
    }
    const step = agNextStep(v, today)
    if (!step) return
    const kind: AgendaKind = step.late > 0 ? 'now' : step.late === 0 ? 'today' : 'soon'
    out.push({ v, kind, rank: step.late > 0 ? 1 : step.late === 0 ? 2 : 4, step, left, miss, ref })
  })
  return out.sort((a, b) => a.rank - b.rank || a.ref.localeCompare(b.ref))
}
/** Some video of the active project comes out in the next 7 days (the empty box then doesn't suggest giving a date) */
export const releaseThisWeek = (videos: Video[], today: string): boolean =>
  videos.some(v => !!v.publishDate && agDiff(v.publishDate, today) >= 0 && agDiff(v.publishDate, today) <= 7)

/** Texts of one row of the box: icon, title, line below, badge; hot = overdue (brand colours) */
interface AgendaRow {
  icon: string
  title: string
  sub: string
  badge: string
  hot: boolean
}
export function agendaRow(it: AgendaItem, statusName: string): AgendaRow {
  const v = it.v, pd = v.publishDate as string
  const out = v.publishTime ? t('{date} at {time}', { date: agShort(pd), time: v.publishTime }) : agShort(pd)
  const when = t('End of work {date}', { date: out })
  let icon: string, title: string, sub: string, badge: string, hot = false
  if (it.kind === 'overdue') {
    icon = 'fa-hourglass-end'; hot = true
    title = t('End of work passed')
    sub = t('It was due {date} and is still "{status}"', { date: out, status: statusName })
    badge = t('Overdue by {time}', { time: tlDur(it.ms || 0) })
  } else if (it.kind === 'check') {
    icon = 'fa-list-check'; title = t('Checklist to complete'); sub = when
    badge = it.left === 0 ? t('Today') : tn(it.left || 0, 'In {n} day', 'In {n} days')
  } else {
    const st = it.step as AgPlanStep & { late: number }
    icon = st.icon; sub = when
    title = it.kind === 'now' ? t('{step} (suggested by {date})', { step: st.txt, date: agShort(st.due) }) : t('{step} by {date}', { step: st.txt, date: agShort(st.due) })
    badge = it.kind === 'now' ? t('To do now') : it.kind === 'today' ? t('Today') : tn(-st.late, 'In {n} day', 'In {n} days')
  }
  if (it.miss && it.miss.length) sub = t('{text} · Missing: {items}', { text: sub, items: it.miss.join(', ') })
  return { icon, title, sub, badge, hot }
}
